// Mapping between Organisation > Settings form values and
//   GET / POST / PUT {domain}monitoring/punch-processing/
// The response groups its fields into sections: general, punch_processing
// and processing_scope. The General card (date format, time zone, language)
// is hidden, so its values stay local and are carried through untouched.

export const organisationPunchProcessingUrl = (domain) => `${domain}monitoring/punch-processing/`;

// Form value -> API value. CURRENT_TENANT comes from the backend sample.
// ALL_APPLICABLE_TENANTS is assumed until the backend confirms the name for
// "All Applicable Companies"; change it here only.
export const SCOPE_TO_API = {
  current: "CURRENT_TENANT",
  all: "ALL_APPLICABLE_TENANTS",
};

// Reads either naming (..._TENANT / ..._COMPANY / ..._COMPANIES) so a backend
// rename keeps loading correctly: anything starting with ALL is "all".
export const scopeFromApi = (value) =>
  String(value ?? "").trim().toUpperCase().startsWith("ALL") ? "all" : "current";

// The fields this screen edits, read from their sections. The older flat
// response, with the same fields at the top level, is still understood.
const editableFields = (data) => ({
  ...data,
  ...data?.punch_processing,
  ...data?.processing_scope,
});

export const isPunchProcessingResponse = (data) =>
  data != null && editableFields(data).minimum_punch_difference_minutes != null;

// minimum_punch_difference_minutes is the same-type gap (IN → IN, OUT → OUT);
// minimum_in_out_punch_difference_minutes is the IN → OUT / OUT → IN gap.
// A record saved before the IN-OUT field existed may lack it, so the form's
// default is kept then.
export const organisationSettingsFromApi = (data, base) => {
  const fields = editableFields(data);
  return {
    ...base,
    minimumPunchDifference: String(fields.minimum_punch_difference_minutes),
    minimumInOutPunchDifference: String(
      fields.minimum_in_out_punch_difference_minutes ?? base.minimumInOutPunchDifference,
    ),
    attendanceProcessingDelay: String(fields.attendance_processing_delay_minutes),
    manualProcessingScope: scopeFromApi(fields.manual_processing_scope),
    reprocessingScope: scopeFromApi(fields.reprocessing_scope),
  };
};

// Sent in the same sections the server returns. manual_changes, the general
// section and is_default are read-only on the server.
export const organisationSettingsToApi = (form) => ({
  punch_processing: {
    minimum_punch_difference_minutes: Number(form.minimumPunchDifference),
    minimum_in_out_punch_difference_minutes: Number(form.minimumInOutPunchDifference),
    attendance_processing_delay_minutes: Number(form.attendanceProcessingDelay),
  },
  processing_scope: {
    manual_processing_scope: SCOPE_TO_API[form.manualProcessingScope],
    reprocessing_scope: SCOPE_TO_API[form.reprocessingScope],
  },
});
